import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { BfxDialogData } from "../../bfx-dialogs/models/bfx-dialog-data.model";
import { BfxDialogCommand } from "../../bfx-dialogs/models/bfx-dialog-command.enum";
import {
  BfxPublicProfileSaveActionEvent,
  ENTITY_ALREADY_EXIST,
  PUBLIC_PROFILE_DEFAULT_RESERVED,
} from "../models/user-profile-public";
import { PublicProfilePromiseActionService } from "shared/cubiq-services";
import {
  BfxSaveProfileNotificationService,
  PROFILE_TYPE,
} from "./bfx-save-profile-notification.service";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { TranslateService } from "@ngx-translate/core";
import { GoldenLayoutService } from "../../golden-layout/golden-layout-service";
import LoggerService from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class BfxPublicProfileService {
  constructor(
    private publicProfileActionService: PublicProfilePromiseActionService,
    private saveProfileNotificationService: BfxSaveProfileNotificationService,
    private translationService: TranslateService,
    private goldenLayoutService: GoldenLayoutService,
  ) {}

  // Observable string sources
  private publicProfileActionSubscription = new Subject<BfxDialogData>();
  // Observable string streams
  public dialogCtrl$ = this.publicProfileActionSubscription.asObservable();
  private publicProfileName: string = null;
  private layout: string = null;

  savePublicUserProfile(): void {
    this.publicProfileActionSubscription.next(
      new BfxDialogData(
        BfxDialogCommand.DIALOG_PUBLIC_PROFILE_SAVE,
        null,
        new BfxPublicProfileSaveActionEvent(this.onSavePublicProfileCallback.bind(this)),
        null,
      ),
    );
  }

  /**
   * Open a new Switch Profile dialog and handle the callbacks
   */
  switchProfile(): void {
    logger.out.info("pprof-s - Switch Profile dialog requested");
    this.publicProfileActionSubscription.next(
      new BfxDialogData(BfxDialogCommand.DIALOG_SWITCH_PROFILE, null, null, {
        callback: (platformProfile: CubiqTS.PlatformProfileImpl) => {
          this.onSwitchProfileConfirm(platformProfile);
        },
        callbackNegative: () => {
          logger.out.info("pprof-s - Switch Profile cancelled");
        },
      }),
    );
  }

  /**
   * Send off the Switch Profile request to the server
   * @param profile
   */
  onSwitchProfileConfirm(profile: CubiqTS.PlatformProfileImpl) {
    logger.out.info("pprof-s - Switch Profile confirm called");
    this.publicProfileActionService.switchProfile(profile);
  }

  onSavePublicProfileCallback(profileName: string) {
    this.goldenLayoutService.getLayoutContent().then((layout) => {
      this.layout = layout;
      this.publicProfileActionService
        .savePublicProfile(profileName, layout, false)
        .then((res: CubiqTS.ErrorsImpl | null) => {
          if (res != null && Array.isArray(res.errorMessages) && res.errorMessages.length > 0) {
            let errorObject: any = JSON.parse(res.errorMessages[0] + "");
            if (errorObject.id && errorObject.id == ENTITY_ALREADY_EXIST) {
              this.publicProfileName = errorObject.args[0];
              this.publicProfileActionSubscription.next(
                new BfxDialogData(
                  BfxDialogCommand.DIALOG_GENERIC_CONFIRM_OPEN,
                  null,
                  null,
                  this.createOverwritePayload(
                    this.translationService.instant("public.profile." + errorObject.id),
                  ),
                ),
              );
            }

            if (errorObject.id && errorObject.id == PUBLIC_PROFILE_DEFAULT_RESERVED) {
              this.publicProfileName = errorObject.args[0];
              this.publicProfileActionSubscription.next(
                new BfxDialogData(
                  BfxDialogCommand.DIALOG_GENERIC_OK,
                  null,
                  null,
                  this.createOverwritePayload(this.translationService.instant(errorObject.id)),
                ),
              );
            }

            this.saveProfileNotificationService.notifyFailureOnFooter(
              PROFILE_TYPE.USER_PROFILE_PUBLIC,
            );
          } else
            this.saveProfileNotificationService.notifySuccessOnFooter(
              PROFILE_TYPE.USER_PROFILE_PUBLIC,
            );
        });
    });
  }

  onOverwritePublicProfileConfirmationCallback() {
    this.publicProfileActionService
      .savePublicProfile(this.publicProfileName, this.layout, true)
      .then((res: CubiqTS.ErrorsImpl | null) => {
        if (res === null)
          this.saveProfileNotificationService.notifySuccessOnFooter(
            PROFILE_TYPE.USER_PROFILE_PUBLIC,
          );
        else
          this.saveProfileNotificationService.notifyFailureOnFooter(
            PROFILE_TYPE.USER_PROFILE_PUBLIC,
          );
      });
  }

  createOverwritePayload(warningMessages): any {
    let payload = {
      messages: [warningMessages],
      title: this.translationService.instant("public.profile.overwrite.header"),
      showConfirmationMessage: true,
      showLogoutMessage: false,
      callback: this.onOverwritePublicProfileConfirmationCallback.bind(this),
    };
    return payload;
  }
}
